import Vue from "vue";
import VueRouter from "vue-router";
import Layout from "@/components/layout/Layout";
import Login from "@/views/Login";
import DashboardV2 from "@/components/dashboard/DashboardV2";
import Dashboard from "@/components/dashboard/Dashboard.vue";
import store from "@/store";
import { clearPersistedAuthState, extractAuthPayloadFromLocation } from "@/Services/autoLogin";
import { useAppUsage } from "@/composables/useAppUsage";

Vue.use(VueRouter);

const routes = [
  // Public Sign-in page
  {
    path: "/signin",
    name: "SignIn",
    component: Login,
    meta: { public: true, title: "Sign in" },
  },

  // Privacy Policy (public page)
  {
    path: "/privacy-policy",
    name: "PrivacyPolicy",
    component: () =>
      import(
        /* webpackChunkName: "PrivacyPolicy" */ "@/views/PrivacyPolicy"
      ),
    meta: { public: true, title: "Privacy Policy" },
  },

  // Legacy redirect for old /login links
  { path: "/login", redirect: { name: "SignIn" } },

  {
    path: "/",
    component: Layout,
    children: [
      {
        path: "",
        redirect: { name: "dashboard" },
      },
      {
        path: "dashboard",
        name: "dashboard",
        component: DashboardV2,
      },
      //sales reports
      {
        path: "sales-dashboard",
        name: "SalesDashboard",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "SalesDashboard" */ "@/components/dashboard/SalesDashboard"
          ),
      },
      {
        path: "view-invoices",
        name: "ViewInvoices",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "viewInvoices" */ "@/components/screens/Sales/Invoices/ViewInvoices"
          ),
      },
      {
        path: "view-orders",
        name: "purchaseOrders",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "purchaseOrders" */ "@/components/screens/Stock/purchaseOrders"
          ),
      },
      {
        path: "view-invoice",
        name: "ViewInvoice",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "viewInvoice" */ "@/components/screens/Sales/Invoices/ViewInvoice"
          ),
      },
      {
        path: "view-uncashed-up-sales",
        name: "ViewUnCashedUp",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "ViewUnCashedUp" */ "@/components/screens/Sales/Invoices/ViewUnCashedUp.vue"
          ),
      },
      {
        path: "sales-reports",
        name: "salesReports",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "salesReports" */ "@/components/screens/Sales/SalesReports"
          ),
      },
      {
        path: "multi-store-department-performance",
        name: "MultiStoreDepartmentPerformance",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "MultiStoreDepartmentPerformance" */ "@/components/screens/Sales/MultiStoreDepartmentPerformance.vue"
          ),
      },
      {
        path: "sales-person-reports",
        name: "SalesPerson",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "SalesPerson" */ "@/components/screens/Sales/SalesPerson"
          ),
      },
      {
        path: "Sales-by-Cashiers-report",
        name: "SalesByCashiers",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "SalesbyCashiers" */ "@/components/screens/Sales/SalesByCashiers.vue"
          ),
      },
      {
        path: "online-vs-offline-sales",
        name: "OnlineVsOfflineSales",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "OnlineVsOfflineSales" */ "@/components/screens/Sales/OnlineVsOfflineSales.vue"
          ),
      },
      {
        path: "top-sell-by-Category",
        name: "topSellByCategory",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "topSellByCategory" */ "@/components/screens/Sales/TopSellByCategory"
          ),
      },
      {
        path: "waiter-reports",
        name: "waiterReports",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "waiterReports" */ "@/components/screens/Sales/WaiterReports"
          ),
      },
      {
        path: "waiter-activity",
        name: "waiterActivity",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "waiterActivity" */ "@/components/screens/Sales/WaiterReports"
          ),
      },
      {
        path: "warnings-reports",
        name: "WarningsReports",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "WarningsReports" */ "@/components/screens/WarningsReports/WarningsReportsViewCashUpClone.vue"
          ),
      },
      {
        path: "warnings-reports/:id",
        name: "WarningsReportDetails",
        props: true,
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "WarningsReportDetails" */ "@/components/screens/WarningsReports/WarningsReportDetails.vue"
          ),
      },
      {
        path: "table-efficiency",
        name: "tableEfficiency",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "tableEfficiency" */ "@/components/screens/Sales/WaiterReports"
          ),
      },
      {
        path: "order-processing",
        name: "orderProcessing",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "orderProcessing" */ "@/components/screens/Sales/WaiterReports"
          ),
      },
      {
        path: "performance-comparison",
        name: "performanceComparison",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "performanceComparison" */ "@/components/screens/Sales/WaiterReports"
          ),
      },
      {
        path: "collection-efficiency",
        name: "collectionEfficiency",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "collectionEfficiency" */ "@/components/screens/Sales/WaiterReports"
          ),
      },
      {
        path: "table-audit",
        name: "tableAudit",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "tableAudit" */ "@/components/screens/Sales/WaiterReports"
          ),
      },
      {
        path: "shift-analysis",
        name: "shiftAnalysis",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "shiftAnalysis" */ "@/components/screens/Sales/WaiterReports"
          ),
      },

      //stoke reports
      {
        path: "stock-dashboard",
        name: "StockDashboard",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "StockDashboard" */ "@/components/dashboard/StockDashboardV2.vue"
          ),
      },
      {
        path: "items-sold-below-cost",
        name: "ItemsSoldBelowCostReport",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "ItemsSoldBelowCostReport" */ "@/components/screens/Stock/ItemsSoldBelowCostReport.vue"
          ),
      },
      {
        path: "stock-dashboard2",
        name: "StockDashboard2",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "StockDashboard" */ "@/components/dashboard/StockDashboard.vue"
          ),
      },
      {
        path: "stock-reports",
        name: "StockReports",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "StockReports" */ "@/components/screens/Stock/StockReports"
          ),
      },
      {
        path: "reorder-stock",
        name: "ReOrderStock",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "ReOrderStock" */ "@/components/screens/Stock/ReorderStock"
          ),
      },
      {
        path: "grv-reports",
        name: "GrvReports",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "GrvReports" */ "@/components/screens/Stock/GrvReports"
          ),
      },
      {
        path: "stock-transfer-received",
        name: "StockTransferReceived",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "StockTransferReceived" */ "@/components/screens/Stock/StockTransferReceived"
          ),
      },
      {
        path: "stock-transfer-sent",
        name: "StockTransferSent",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "StockTransferSent" */ "@/components/screens/Stock/StockTransferSent"
          ),
      },
      {
        path: "stockTake-report",
        name: "stockTakeReport",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "StockTakeReport" */ "@/components/screens/Stock/StockTakeReport"
          ),
      },
      {
        path: "adjustment-report",
        name: "StockAdjustment",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "StockAdjustment" */ "@/components/screens/Stock/StockAdjustmentReport"
          ),
      },
      //stock item rp
      {
        path: "/stockItem-reports/",
        name: "StockItemReports",
        props: true,
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "StockItemReports" */ "@/components/screens/Stock/StockItemReport"
          ),
      },
      {
        path: "/stockItem-reports/:tab",
        name: "StockItemReportsWithTab",
        props: true,
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "StockItemReports" */ "@/components/screens/Stock/StockItemReport"
          ),
      },
      {
        path: "item-links",
        name: "ItemLinks",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "ItemLinks" */ "@/components/screens/Stock/StockLinks"
          ),
      },
      {
        path: "stockItem-history",
        name: "StockHistory",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "StockHistory" */ "@/components/screens/Stock/StockItemHistory"
          ),
      },

      //finance
      {
        path: "finance-dashboard",
        name: "FinanceDashboard",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "FinanceDashboard" */ "@/components/dashboard/FinanceDashboard.vue"
          ),
      },
      {
        path: "finance",
        name: "finance",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "finance" */ "@/components/screens/Finance/financeReports"
          ),
      },
      {
        path: "view-expenses",
        name: "ExpensesByCat",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "ExpensesByCat" */ "@/components/screens/Finance/ExpensesByCat"
          ),
      },
      {
        path: "view-expenses-dash",
        name: "ExpenseDash",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "ExpensesByCat" */ "@/components/dashboard/ExpenseDash.vue"
          ),
      },
      //payouts
      {
        path: "all-payouts",
        name: "allPayouts",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "allPayouts" */ "@/components/screens/Finance/payouts/AllPayouts"
          ),
      },

      //safes and accounts
      {
        path: "money-movement-reports",
        name: "MoneyMovements",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "MoneyMovements" */ "@/components/screens/Finance/Safes & Accounts/MoneyMovement.vue"
          ),
      },
      {
        path: "view-safes",
        name: "ViewSafes",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "ViewSafes" */ "@/components/screens/Finance/Safes & Accounts/ViewSafes"
          ),
      },
      //ledgers
      {
        path: "view-ledgers",
        name: "Ledgers",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "Ledgers" */ "@/components/screens/Finance/Ledgers & Returns/Ledgers"
          ),
      },
      {
        path: "view-returns",
        name: "Returns",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "Returns" */ "@/components/screens/Finance/Ledgers & Returns/Returns"
          ),
      },

      //statements
      {
        path: "income-statement",
        name: "IncomeStatement",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "IncomeStatement" */ "@/components/screens/Finance/Statements/IncomeStatement"
          ),
      },
      {
        path: "view-statement-details",
        name: "ViewIncomeAccount",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "ViewIncomeAccount" */ "@/components/screens/Finance/Statements/ViewIncomeAccount"
          ),
      },
      {
        path: "cash-flow-statement",
        name: "cashFlowStatement",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "cashFlowStatement" */ "@/components/screens/Finance/Statements/CashFlowStatement"
          ),
      },
      {
        path: "view-cash-flow-details",
        name: "cashFlowDetails",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "cashFlowStatement" */ "@/components/screens/Finance/Statements/CashFlowStatement"
          ),
      },
      {
        path: "vat-reports",
        name: "vatReports",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "vatReports" */ "@/components/screens/Finance/vatReports"
          ),
      },

      //accounts
      {
        path: "accounts-reports",
        name: "accountsReports",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "accountsReports" */ "@/components/screens/accounts/accountsReports"
          ),
      },
      {
        path: "debtors-reports",
        name: "debtors",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "debtors" */ "@/components/screens/accounts/Debtors"
          ),
      },
      {
        path: "creditors-reports",
        name: "creditors",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "creditors" */ "@/components/screens/accounts/Creditors"
          ),
      },
      {
        path: "combined-creditors-reports",
        name: "combinedCreditors",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "combinedCreditors" */ "@/components/screens/accounts/combinedCreditors"
          ),
      },
      {
        path: "combined-debtors-reports",
        name: "combinedDebtors",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "combinedDebtors" */ "@/components/screens/accounts/combinedDebtors"
          ),
      },
      {
        path: "accounts-reports/ar-ap-transactions",
        name: "arApCombinedTransactions",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "arApCombinedTransactions" */ "@/components/screens/accounts/ArApCombinedTransactions.vue"
          ),
      },
      {
        path: "accounts-reports/debtor-activity",
        name: "debtor-activity",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "debtorActivity" */ "@/components/screens/accounts/DebtorActivity.vue"
          ),
      },
      //cashup
      {
        path: "cashUp-dashboard",
        name: "CashUpDashboard",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "CashUpDashboard" */ "@/components/dashboard/CashUpDashboard.vue"
          ),
      },
      {
        path: "all-store-cashUps",
        name: "viewStoreCashUps",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "viewStoreCashUps" */ "@/components/screens/CashUp/ViewCashUps"
          ),
      },
      {
        path: "store-cashUp-details",
        name: "storeCashUpDetails",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "storeCashUpDetails" */ "@/components/screens/CashUp/storeCashUpDetails"
          ),
      },
      //super v
      {
        path: "supervisor-dashboard",
        name: "SupervisorDashboard",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "SupervisorDashboard" */ "@/components/dashboard/SupervisorDashboard.vue"
          ),
      },
      {
        path: "supervisor-reports-details",
        name: "supervisorDetails",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "supervisorDetails" */ "@/components/screens/Supervisor/SupervisorDetails"
          ),
      },
      {
        path: "supervisor-user-stats",
        name: "UserStats",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "UserStats" */ "@/components/screens/Supervisor/UserStats.vue"
          ),
      },
      {
        path: "supervisor-voids-reports",
        name: "supervisorVoids",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "supervisorVoids" */ "@/components/screens/Supervisor/Voids.vue"
          ),
      },
      {
        path: "supervisor-refunds-reports",
        name: "supervisorRefunds",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "supervisorRefunds" */ "@/components/screens/Supervisor/Refunds.vue"
          ),
      },
      {
        path: "supervisor-discounts-reports",
        name: "supervisorDiscounts",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "supervisorDiscounts" */ "@/components/screens/Supervisor/Discounts.vue"
          ),
      },
      {
        path: "supervisor-priceChange-reports",
        name: "PriceChange",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "PriceChanges" */ "@/components/screens/Supervisor/PriceChanges.vue"
          ),
      },
      {
        path: "supervisor-incoming-stock-reports",
        name: "IncomingStock",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "IncomingStock" */ "@/components/screens/Supervisor/IncomingStock.vue"
          ),
      },
      {
        path: "supervisor-open-tables-reports",
        name: "OpenTablesQuotes",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "OpenTablesQuotes" */ "@/components/screens/Supervisor/OpenTablesQuotes.vue"
          ),
      },
      {
        path: "supervisor-payouts-reports",
        name: "Payouts",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "Payouts" */ "@/components/screens/Supervisor/Payouts.vue"
          ),
      },
      {
        path: "supervisor-sold-below-cost-reports",
        name: "SoldBelowCost",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "SoldBelowCost" */ "@/components/screens/Supervisor/SoldBelowCost.vue"
          ),
      },
      {
        path: "/stock-take-variances",
        name: "StockTakeVars",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "StockTakeVars" */ "@/components/screens/Supervisor/StockTakeVars.vue"
          ),
      },
      {
        path: "supervisor-un-cashed-up-reports",
        name: "UnCashedUp",
        component: () =>
          import(
            /* webpackPrefetch: true, webpackChunkName: "UnCashedUp" */ "@/components/screens/Supervisor/UnCashedUp.vue"
          ),
      },
      {
        path: "help",
        name: "help",
        component: () =>
          import(
            /* webpackChunkName: "help" */ "@/views/Help/HelpView.vue"
          ),
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

let autoLoginHandled = false;
router.beforeEach(async (to, from, next) => {
  try {
    if (!autoLoginHandled) {
      const payload = extractAuthPayloadFromLocation();
      if (payload) {
        autoLoginHandled = true;

        try {
          await clearPersistedAuthState();
          store.commit('resetRootState');
        } catch (e) {
          console.warn('[autoLogin] Failed to clear existing state:', e);
        }

        try {
          await store.dispatch('franchiseLogin', {
            email: payload.auth.email,
            password: payload.auth.password,
          });
        } catch (e) {
          console.warn('[autoLogin] Login failed:', e);
        }

        if (payload.storeDB) {
          try {
            const stores = store.getters.getStores || [];
            const match = stores.find(s => s.StoreDB === payload.storeDB);
            if (match) {
              store.commit('setStore', match);
            } else {
              console.warn('[autoLogin] storeDB not found among loaded stores:', payload.storeDB);
            }
          } catch (e) {
            console.warn('[autoLogin] Failed to select storeDB:', e);
          }
        }

        const target = {
          name: payload.route.name,
          params: payload.route.params || {},
          query: { ...(payload.route.query || {}) }
        };
        // Remove auth credentials from query string for safety
        delete target.query.auth;
        // Backward compatibility: old deep links may reference 'Login' route name
        if (target.name === 'Login') target.name = 'SignIn';

        return next({ ...target, replace: true });
      }
    }
  } catch (e) {
    console.warn('[autoLogin] Unexpected error:', e);
  }

  let user = localStorage.getItem('franchiseUser');
  const isPublic = to.matched.some(r => r.meta && r.meta.public);

  // If not authenticated and route is not public, go to SignIn
  if (!user && !isPublic && to.name !== 'SignIn') {
    return next({ name: 'SignIn', query: { redirect: to.fullPath } });
  }

  // If authenticated and trying to access SignIn, send to dashboard
  if (user && to.name === 'SignIn') {
    return next({ name: 'dashboard' });
  }

  // Track page views using App Usage API (only for authenticated users)
  try {
    const { queueEvent, setUser } = useAppUsage()

    // Only track after store is hydrated and user is authenticated
    const currentUser = store.getters.getCurrentUser
    const isAuthenticated = currentUser && (currentUser.UserID || currentUser.id) &&
      currentUser.Username && String(currentUser.Username).trim().length > 0

    // Only track page views for authenticated users (after hydration)
    if (store.getters.isHydrated && isAuthenticated) {
      const userId = currentUser.UserID || currentUser.id
      setUser(userId)

      // Queue event for batch processing (not immediate API call)
      if (to.name) {
        queueEvent('page:' + to.name, 'view')
      }
    }
  } catch (e) {
    // Silently ignore tracking errors - should not affect navigation
  }

  next();
});

export default router;
